import _ from "lodash";
import { useMemo } from "react";

import PaginationControlsV2 from "../../../../../global/PaginationControlsV2";
import useModifyProviderDetails from "../../hooks/useModifyProviderDetails";
import useProviderStatusHistoryData from "../../hooks/useProviderStatusHistoryData";
import { useProviderDetailStore } from "../../store/provider-details/providerDetailStore";
import CancelButton from "../buttons/CancelButton";
import SaveButton from "../buttons/SaveButton";
import CheckBox from "../fields/CheckBox";
import FieldLabel from "../provider-details/FieldLabel";

import NetworkSettingsHistoryTable from "./NetworkSettingsHistoryTable";

const NETWORK_SETTINGS_FIELDS = ["inNetwork", "receivingClients", "acceptsFunding"];

export default function NetworkSettings() {
  const { initialData, modifiedData, setModifiedData } = useProviderDetailStore();
  const { handleFieldOnChangeValue, handleOnClickSaveButton } = useModifyProviderDetails();
  const {
    data,
    loading,
    currentPage,
    resLimit,
    resultCount,
    fetchProviderStatusHistory,
    setCurrentPage,
  } = useProviderStatusHistoryData();

  const isDirty = useMemo(() => {
    const initialValues = _.pick(initialData, NETWORK_SETTINGS_FIELDS);
    const modifiedValues = _.pick(modifiedData, NETWORK_SETTINGS_FIELDS);
    return !_.isEqual(initialValues, modifiedValues);
  }, [initialData, modifiedData]);

  const handleOnClickCancelButton = () => {
    setModifiedData({ ...modifiedData, ..._.pick(initialData, NETWORK_SETTINGS_FIELDS) });
  };

  const handlePageChange = (_, page) => {
    setCurrentPage(page);
  };

  const handleOnClickSave = async () => {
    await handleOnClickSaveButton();
    fetchProviderStatusHistory();
  };

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <div className="flex flex-col gap-1">
          <FieldLabel value="Network Status" />
          <CheckBox
            label="In Network"
            checked={Boolean(modifiedData?.inNetwork)}
            readOnly={true}
            handleClick={(checked) => handleFieldOnChangeValue("inNetwork", checked)}
          />
        </div>
        <div className="flex flex-col gap-1">
          <FieldLabel value="Receiving Clients" />
          <CheckBox
            label="Receiving Clients"
            checked={Boolean(modifiedData?.receivingClients)}
            handleClick={(checked) => handleFieldOnChangeValue("receivingClients", checked)}
          />
        </div>
        <div className="flex flex-col gap-1">
          <FieldLabel value="Accepts Funding" />
          <CheckBox
            label="Accepts Funding"
            checked={Boolean(modifiedData?.acceptsFunding)}
            handleClick={(checked) => handleFieldOnChangeValue("acceptsFunding", checked)}
          />
        </div>
      </div>

      <div className="mt-8 flex min-h-0 flex-1 flex-col">
        <div className="min-h-0 flex-1 overflow-y-auto pr-2 [&::-webkit-scrollbar-thumb]:bg-blue-700 [&::-webkit-scrollbar]:[width:6px]">
          <NetworkSettingsHistoryTable data={data} loading={loading} />
        </div>

        {resultCount > resLimit ? (
          <div className="flex justify-center py-4">
            <PaginationControlsV2
              resultCount={resultCount}
              currentPage={currentPage}
              resultLimit={resLimit}
              handleChange={handlePageChange}
            />
          </div>
        ) : (
          <div className="py-4" />
        )}
      </div>

      <div className="pt-2">
        <div className="ml-auto grid w-full grid-cols-1 gap-4 md:w-1/2 md:grid-cols-2">
          <CancelButton disabled={!isDirty} onClick={handleOnClickCancelButton} />
          <SaveButton disabled={!isDirty} onClick={handleOnClickSave} label="Save" />
        </div>
      </div>
    </div>
  );
}
